import type { Mutation } from "@tanstack/vue-query"
import axios from "axios"

// Only scalar, categorical properties are allowed on analytics events: mutation variables often
// carry business payloads (contacts, purchase orders, amounts) that must never reach PostHog.
export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>

/**
 * Captures a PostHog product-analytics event (snake_case name). A no-op when PostHog is not
 * configured, and never throws: analytics must not break the feature it observes.
 */
export function captureEvent(event: string, properties?: AnalyticsProperties) {
  try {
    tryUseNuxtApp()?.$posthog?.capture(event, properties)
  } catch {
    // Swallow SDK errors, see above.
  }
}

export interface MutationAnalytics<TVariables = unknown> {
  /** Success event name in snake_case; failures append `_failed`. */
  event: string
  properties?: (variables: TVariables) => AnalyticsProperties
}

/**
 * Declares the PostHog event emitted when a mutation settles. Spread into `useMutation` options:
 *
 *   useMutation({
 *     mutationFn,
 *     ...trackMutation<Variables>("invoice_published", (v) => ({ invoice_count: v.uuids.length })),
 *   })
 *
 * On success `<event>` is captured; on error `<event>_failed` is captured with the same
 * properties plus the HTTP `status_code` (never the error message, which may carry business
 * data). Both captures happen in the global `MutationCache` (see plugins/vue-query.ts).
 */
export function trackMutation<TVariables>(
  event: string,
  properties?: (variables: TVariables) => AnalyticsProperties,
): { meta: { analytics: MutationAnalytics<TVariables> } } {
  return { meta: { analytics: { event, properties } } }
}

export function captureMutationSuccess(variables: unknown, mutation: Mutation<any, any, any>) {
  try {
    const analytics = mutation.meta?.analytics as MutationAnalytics | undefined
    if (!analytics) return

    const posthog = tryUseNuxtApp()?.$posthog
    if (!posthog) return

    posthog.capture(analytics.event, analytics.properties?.(variables))
  } catch {
    // Analytics must not turn a successful mutation into an error or skip cache invalidation.
  }
}

export function captureMutationFailure(
  error: unknown,
  variables: unknown,
  mutation: Mutation<any, any, any>,
) {
  try {
    const analytics = mutation.meta?.analytics as MutationAnalytics | undefined
    if (!analytics) return

    const posthog = tryUseNuxtApp()?.$posthog
    if (!posthog) return

    const statusCode = axios.isAxiosError(error) ? error.response?.status : undefined

    posthog.capture(`${analytics.event}_failed`, {
      ...analytics.properties?.(variables),
      status_code: statusCode,
    })
  } catch {
    // Preserve the original mutation error and let the application's error handlers run.
  }
}
